import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { API, downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Field, TextInput, TextArea, SelectInput, Loader, EmptyState } from "../components/Primitives";

// -----------------------------------------------------------------------------
// Answer + status + risk meta
// -----------------------------------------------------------------------------
const ANSWER_META = {
  yes:            { label: "Yes",  color: "#059669", bg: "rgba(5,150,105,0.10)", ring: "rgba(5,150,105,0.35)", icon: "Check" },
  partial:        { label: "Partial", color: "#D97706", bg: "rgba(217,119,6,0.10)", ring: "rgba(217,119,6,0.35)", icon: "MinusCircle" },
  no:             { label: "No",   color: "#DC2626", bg: "rgba(220,38,38,0.10)", ring: "rgba(220,38,38,0.35)", icon: "X" },
  not_applicable: { label: "N/A",  color: "#6B7280", bg: "rgba(107,114,128,0.10)", ring: "rgba(107,114,128,0.35)", icon: "SlashCircle" },
  unknown:        { label: "Unknown", color: "#0891B2", bg: "rgba(8,145,178,0.10)", ring: "rgba(8,145,178,0.35)", icon: "HelpCircle" },
};
const ANSWER_KEYS = ["yes", "partial", "no", "not_applicable", "unknown"];
const RISK_META = {
  critical: { color: "#DC2626", bg: "rgba(220,38,38,0.10)", label: "Critical" },
  high:     { color: "#EA580C", bg: "rgba(234,88,12,0.10)", label: "High" },
  medium:   { color: "#D97706", bg: "rgba(217,119,6,0.10)", label: "Medium" },
  low:      { color: "#0891B2", bg: "rgba(8,145,178,0.10)", label: "Low" },
  none:     { color: "#059669", bg: "rgba(5,150,105,0.10)", label: "None" },
};
const STATUS_META = {
  open:         { color: "#6B7280", bg: "rgba(107,114,128,0.10)", label: "Open" },
  in_progress:  { color: "#D97706", bg: "rgba(217,119,6,0.10)",  label: "In progress" },
  completed:    { color: "#0891B2", bg: "rgba(8,145,178,0.10)",  label: "Completed" },
  verified:     { color: "#059669", bg: "rgba(5,150,105,0.10)",  label: "Verified" },
};

// Fields shown in the Company Profile wizard step. Kept to the ones that
// actually drive DPDP question routing so onboarding stays fast.
const PROFILE_ROUTING_FIELDS = [
  { key: "legal_name",              section: "basic",           type: "text",   label: "Legal company name", required: true },
  { key: "industry",                section: "business",        type: "select", label: "Industry" },
  { key: "business_model",          section: "business",        type: "multiselect", label: "Business model", options: ["B2B", "B2C", "B2B2C", "B2G", "Internal enterprise"] },
  { key: "employee_count",          section: "business",        type: "select", label: "Number of employees" },
  { key: "customer_count",          section: "business",        type: "select", label: "Number of customers / users" },
  { key: "vendor_count",            section: "business",        type: "select", label: "Number of vendors / third parties" },
  { key: "handles_children_data",   section: "business",        type: "boolean", label: "Handle children's data (users < 18)?" },
  { key: "handles_employee_data",   section: "business",        type: "boolean", label: "Process employee personal data?" },
  { key: "sensitive_data_types",    section: "business",        type: "multiselect", label: "Sensitive / high-risk personal data handled" },
  { key: "cloud_providers",         section: "technology",      type: "multiselect", label: "Cloud providers used" },
  { key: "ai_llm_tools",            section: "technology",      type: "list_text", label: "AI / LLM tools used", help: "e.g. OpenAI, Anthropic, GitHub Copilot" },
  { key: "cross_border_transfer",   section: "data_processing", type: "boolean", label: "Transfer data outside India?" },
  { key: "data_shared_with_vendors", section: "data_processing", type: "boolean", label: "Share personal data with vendors?" },
  { key: "has_dpo",                 section: "basic",           type: "boolean", label: "Have a Data Protection Officer?" },
];

// Which flag drives which section — mirrors backend SECTION_APPLICABILITY
const SECTION_FLAG_HINT = {
  "Data Processor Involvement":   ["uses_vendors"],
  "Children's Data":              ["handles_children_data"],
  "Vendor & Processor Compliance": ["uses_vendors"],
  "Cross-Border Transfer":        ["cross_border_transfer"],
  "SDF Readiness":                ["is_significant_data_fiduciary", "is_large_enterprise"],
};

const HUMAN_FLAG = {
  handles_children_data: "handles children's data",
  handles_employee_data: "processes employee data",
  handles_sensitive_data: "handles sensitive personal data",
  cross_border_transfer: "transfers data outside India",
  uses_vendors: "uses vendors / third parties",
  uses_ai_tools: "uses AI / LLM tools",
  uses_cloud: "uses cloud infrastructure",
  is_large_enterprise: "is a large enterprise",
  is_significant_data_fiduciary: "may be a Significant Data Fiduciary",
  has_dpo: "has a DPO",
};


// -----------------------------------------------------------------------------
// Profile step: compact wizard for the routing fields
// -----------------------------------------------------------------------------
function ProfileStep({ schema, profile, onSave, onSkip, onNext }) {
  const rows = useMemo(() => {
    if (!schema) return [];
    const map = {};
    schema.sections.forEach(s => s.fields.forEach(f => { map[f.key] = f; }));
    return PROFILE_ROUTING_FIELDS.map(prf => {
      const meta = map[prf.key];
      return {
        ...prf,
        options: prf.options || meta?.options,
        type: prf.type || meta?.type,
        help: prf.help || meta?.help,
      };
    });
  }, [schema]);

  const [values, setValues] = useState(() => {
    const v = {};
    rows.forEach(r => { v[r.key] = profile?.fields?.[r.key]?.value ?? (["multiselect", "list_text"].includes(r.type) ? [] : ""); });
    return v;
  });
  useEffect(() => {
    const v = {};
    rows.forEach(r => { v[r.key] = profile?.fields?.[r.key]?.value ?? (["multiselect", "list_text"].includes(r.type) ? [] : ""); });
    setValues(v);
  }, [profile, rows]);

  const [saving, setSaving] = useState(false);

  const setV = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const doSave = async () => {
    setSaving(true);
    const fields = rows
      .filter(r => {
        const v = values[r.key];
        return !(v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0));
      })
      .map(r => ({
        field_key: r.key,
        value: values[r.key],
        status: "provided",
      }));
    if (fields.length === 0) {
      toast.info("Add at least one field so we can tailor the questionnaire");
      setSaving(false); return;
    }
    try {
      await onSave(fields);
      toast.success("Profile saved · questionnaire tailored");
      onNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const completeness = profile?.completeness?.overall_pct ?? 0;
  const flags = profile?.applicability || {};
  const activeFlags = Object.entries(flags).filter(([, v]) => v).map(([k]) => k);

  return (
    <div className="space-y-5 animate-in">
      {/* Hero */}
      <div className="aurora rounded-2xl p-6 relative overflow-hidden" style={{ border: "1px solid var(--border)" }}>
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
          backgroundImage: "radial-gradient(rgba(79,70,229,0.18) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage: "radial-gradient(280px 180px at 90% 20%, #000, transparent 70%)",
        }} />
        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-[0_6px_20px_-6px_rgba(79,70,229,0.55)]"
            style={{ background: "linear-gradient(135deg, #4F46E5, #7C3AED)" }}>
            <Icons.Building2 className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-cap mb-1">Step 1 of 3 · Onboarding</div>
            <h2 className="text-xl font-semibold ink tracking-tight">Company Profile</h2>
            <p className="text-[12.5px] muted mt-1 leading-relaxed max-w-2xl">
              A few facts about your organization drive the entire DPDP assessment. Fields marked as unknown or empty will keep questions generic — filling them prunes irrelevant sections and adds specific ones (children data, cross-border transfer, AI risk, SDF readiness).
            </p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: "rgba(79,70,229,0.10)", color: "var(--primary)" }}>
                <Icons.CheckCircle2 className="w-3 h-3" /> Full profile {completeness}% complete
              </div>
              {activeFlags.length > 0 && (
                <div className="text-[11px] muted">
                  Detected: {activeFlags.slice(0, 3).map(f => HUMAN_FLAG[f] || f.replace(/_/g, " ")).join(", ")}
                  {activeFlags.length > 3 && ` +${activeFlags.length - 3}`}
                </div>
              )}
              <button onClick={() => window.location.href = "/app/company-profile"} className="ml-auto text-[11px] primary font-medium hover:underline">
                Edit full profile →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Routing fields */}
      <div className="surface rounded-2xl p-6" style={{ border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-cap mb-1">Routing questions</div>
            <div className="text-[13px] ink font-medium">14 quick facts to tailor your DPDP assessment</div>
          </div>
          <div className="text-[11px] muted">{Object.values(values).filter(v => v !== "" && v !== null && !(Array.isArray(v) && v.length === 0)).length} / {rows.length}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map(r => (
            <div key={r.key} className="stagger-in" style={{ "--stagger": rows.indexOf(r) % 4 }}>
              <ProfileFieldInput field={r} value={values[r.key]} onChange={(v) => setV(r.key, v)} />
            </div>
          ))}
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-2">
        <Btn variant="secondary" size="sm" onClick={onSkip} data-testid="wizard-skip-profile">
          <Icons.SkipForward className="w-3.5 h-3.5" /> Skip for now
        </Btn>
        <Btn onClick={doSave} disabled={saving} data-testid="wizard-save-profile-next">
          {saving ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.ArrowRight className="w-3.5 h-3.5" />}
          Save & tailor questions
        </Btn>
      </div>
    </div>
  );
}


function ProfileFieldInput({ field, value, onChange }) {
  const t = field.type;
  const cardCls = "rounded-xl p-3.5 card-hover";
  const cardSty = { border: "1px solid var(--border)", background: "var(--surface)" };

  if (t === "boolean") {
    return (
      <div className={cardCls} style={cardSty}>
        <div className="text-[12.5px] font-medium ink mb-2">{field.label}</div>
        <div className="flex gap-2">
          {[
            { v: true, m: ANSWER_META.yes, lbl: "Yes" },
            { v: false, m: ANSWER_META.no, lbl: "No" },
          ].map(({ v, m, lbl }) => {
            const active = value === v;
            return (
              <button key={String(v)} type="button" onClick={() => onChange(active ? "" : v)}
                className="flex-1 px-3 py-1.5 text-[12px] rounded-lg font-medium transition-all"
                style={active
                  ? { background: m.color, color: "white", boxShadow: `0 4px 12px -3px ${m.ring}` }
                  : { background: "var(--bg-2)", color: "var(--ink)" }}
                data-testid={`pf-input-${field.key}-${lbl.toLowerCase()}`}>
                {lbl}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (t === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className={cardCls} style={cardSty}>
        <div className="text-[12.5px] font-medium ink mb-2">{field.label}</div>
        <div className="flex flex-wrap gap-1.5" data-testid={`pf-input-${field.key}`}>
          {(field.options || []).map(o => {
            const active = arr.includes(o);
            return (
              <button key={o} type="button" onClick={() => onChange(active ? arr.filter(x => x !== o) : [...arr, o])}
                className="px-2.5 py-1 text-[11px] rounded-full border transition-all"
                style={active
                  ? { background: "var(--primary)", color: "white", borderColor: "var(--primary)" }
                  : { background: "var(--surface)", color: "var(--ink)", borderColor: "var(--border)" }}>
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (t === "select") {
    return (
      <div className={cardCls} style={cardSty}>
        <div className="text-[12.5px] font-medium ink mb-2">{field.label}</div>
        <SelectInput value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`pf-input-${field.key}`}>
          <option value="">— Select —</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </SelectInput>
      </div>
    );
  }
  if (t === "list_text") {
    const disp = Array.isArray(value) ? value.join(", ") : (value ?? "");
    return (
      <div className={cardCls} style={cardSty}>
        <div className="text-[12.5px] font-medium ink mb-2">{field.label}</div>
        <TextInput value={disp} onChange={(e) => onChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder={field.help || "Comma-separated"} data-testid={`pf-input-${field.key}`} />
      </div>
    );
  }
  return (
    <div className={cardCls} style={cardSty}>
      <div className="text-[12.5px] font-medium ink mb-2">{field.label} {field.required && <span className="danger">*</span>}</div>
      <TextInput value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`pf-input-${field.key}`} />
    </div>
  );
}


// -----------------------------------------------------------------------------
// Question card — rich, editable, animated
// -----------------------------------------------------------------------------
function QuestionCard({ q, answer, evidenceList, onChange, onUpload, onDeleteEvidence }) {
  const canonical = answer?.answer || "";
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const risk = answer?.risk_level || (canonical ? "medium" : "medium");
  const status = answer?.status || "open";

  const uploadEv = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.info("Pick a file first");
    setUploading(true);
    try {
      await onUpload(q.id, f);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="surface rounded-2xl overflow-hidden card-hover" style={{ border: "1px solid var(--border)" }} data-testid={`q-card-${q.id}`}>
      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: canonical ? ANSWER_META[canonical]?.bg : "var(--bg-2)" }}>
            {canonical ? (
              (() => { const Ico = Icons[ANSWER_META[canonical]?.icon] || Icons.HelpCircle; return <Ico className="w-4 h-4" style={{ color: ANSWER_META[canonical]?.color }} />; })()
            ) : (
              <Icons.HelpCircle className="w-4 h-4 muted-2" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <span className="text-[9.5px] font-semibold mono uppercase tracking-wider muted">{q.id}</span>
              {q.dpdp_ref && <span className="text-[9.5px] mono px-1.5 py-[1px] rounded" style={{ background: "rgba(79,70,229,0.10)", color: "var(--primary)" }}>{q.dpdp_ref}</span>}
              {canonical && (
                <>
                  <span className="text-[9.5px] font-semibold mono uppercase tracking-wider px-1.5 py-[1px] rounded" style={{ background: RISK_META[risk]?.bg, color: RISK_META[risk]?.color }}>{RISK_META[risk]?.label}</span>
                  <span className="text-[9.5px] font-semibold mono uppercase tracking-wider px-1.5 py-[1px] rounded" style={{ background: STATUS_META[status]?.bg, color: STATUS_META[status]?.color }}>{STATUS_META[status]?.label}</span>
                </>
              )}
              {(evidenceList?.length || 0) > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[9.5px] font-medium px-1.5 py-[1px] rounded-full" style={{ background: "rgba(79,70,229,0.10)", color: "var(--primary)" }}>
                  <Icons.Paperclip className="w-2.5 h-2.5" /> {evidenceList.length}
                </span>
              )}
            </div>
            <div className="text-[13px] font-semibold ink leading-snug">{q.question}</div>
            {q.help_text && <div className="text-[11px] muted mt-1 leading-relaxed">{q.help_text}</div>}
          </div>
        </div>

        {/* Answer buttons */}
        <div className="grid grid-cols-5 gap-1.5 mb-3" data-testid={`q-answer-${q.id}`}>
          {ANSWER_KEYS.map(k => {
            const m = ANSWER_META[k];
            const Ico = Icons[m.icon] || Icons.Circle;
            const active = canonical === k;
            return (
              <button key={k} type="button" onClick={() => onChange(q, { answer: k })}
                className="relative flex flex-col items-center gap-1 py-2.5 rounded-xl transition-all duration-200"
                style={active
                  ? { background: m.color, color: "white", boxShadow: `0 6px 20px -6px ${m.ring}` }
                  : { background: "var(--bg-2)", color: "var(--ink)" }}
                data-testid={`q-answer-${q.id}-${k}`}>
                <Ico className="w-4 h-4" />
                <span className="text-[10.5px] font-medium">{m.label}</span>
              </button>
            );
          })}
        </div>

        {/* Toggle to reveal advanced fields */}
        {canonical && (
          <>
            <button onClick={() => setExpanded(x => !x)} className="text-[11px] primary font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`q-expand-${q.id}`}>
              <Icons.Settings2 className="w-3 h-3" /> {expanded ? "Hide" : "Add"} gap · owner · target · evidence
              <Icons.ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>

            {expanded && (
              <div className="mt-3 pt-3 border-t space-y-3" style={{ borderColor: "var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Field label="Risk level">
                    <SelectInput value={risk} onChange={(e) => onChange(q, { risk_level: e.target.value })} data-testid={`q-risk-${q.id}`}>
                      {Object.keys(RISK_META).map(k => <option key={k} value={k}>{RISK_META[k].label}</option>)}
                    </SelectInput>
                  </Field>
                  <Field label="Status">
                    <SelectInput value={status} onChange={(e) => onChange(q, { status: e.target.value })} data-testid={`q-status-${q.id}`}>
                      {Object.keys(STATUS_META).map(k => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
                    </SelectInput>
                  </Field>
                  <Field label="Target date">
                    <TextInput type="date" value={(answer?.target_date || "").slice(0, 10)} onChange={(e) => onChange(q, { target_date: e.target.value })} data-testid={`q-target-${q.id}`} />
                  </Field>
                </div>

                <Field label="Gap description">
                  <TextArea rows={2} value={answer?.gap_description || ""} onChange={(e) => onChange(q, { gap_description: e.target.value })} placeholder="What's missing or partially implemented?" data-testid={`q-gap-${q.id}`} />
                </Field>

                <Field label="Recommended action">
                  <TextArea rows={2} value={answer?.recommended_action || q.recommendation || ""} onChange={(e) => onChange(q, { recommended_action: e.target.value })} placeholder="Concrete remediation step" data-testid={`q-rec-${q.id}`} />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Field label="Responsible owner (email)">
                    <TextInput type="email" value={answer?.responsible_owner || ""} onChange={(e) => onChange(q, { responsible_owner: e.target.value })} placeholder="dpo@company.com" data-testid={`q-owner-${q.id}`} />
                  </Field>
                  <Field label="Notes">
                    <TextInput value={answer?.notes || ""} onChange={(e) => onChange(q, { notes: e.target.value })} placeholder="Optional context" data-testid={`q-notes-${q.id}`} />
                  </Field>
                </div>

                {q.evidence_examples && (
                  <div className="text-[10.5px] muted italic">
                    <Icons.Info className="w-3 h-3 inline mr-0.5" />
                    Suggested evidence: {q.evidence_examples}
                  </div>
                )}

                <div className="rounded-lg p-3" style={{ border: "1px dashed var(--border-strong)", background: "var(--bg-2)" }}>
                  <div className="flex items-center gap-2">
                    <input ref={fileRef} type="file" className="text-xs flex-1" data-testid={`q-evidence-file-${q.id}`} />
                    <Btn size="sm" onClick={uploadEv} disabled={uploading} data-testid={`q-evidence-upload-${q.id}`}>
                      {uploading ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.UploadCloud className="w-3.5 h-3.5" />} Upload
                    </Btn>
                  </div>
                  {(evidenceList?.length || 0) > 0 && (
                    <div className="mt-2 space-y-1">
                      {evidenceList.map(ev => (
                        <div key={ev.id} className="flex items-center justify-between p-1.5 rounded" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                          <div className="min-w-0 flex-1 truncate">
                            <span className="text-[11.5px] ink font-medium">{ev.label || ev.filename}</span>
                            <span className="text-[10px] muted ml-2">{(ev.size / 1024).toFixed(1)} KB</span>
                          </div>
                          <button onClick={() => onDeleteEvidence(q.id, ev.id)} className="p-1 rounded hover:bg-app-2 danger" title="Delete" data-testid={`q-evidence-del-${q.id}-${ev.id}`}>
                            <Icons.Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Section step — one DPDP section at a time
// -----------------------------------------------------------------------------
function SectionStep({ section, questions, answers, evidenceMap, onChange, onUpload, onDeleteEvidence, onPrev, onNext, isLast, breakdown, appliesBecause }) {
  const sec = breakdown?.find(b => b.section === section.section);
  const progressPct = sec?.score ?? 0;

  return (
    <div className="space-y-5 animate-in">
      <div className="aurora-tight rounded-2xl p-5" style={{ border: "1px solid var(--border)" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="label-cap mb-1">{section.dpdp_ref}</div>
            <h2 className="text-xl font-semibold ink tracking-tight">{section.section}</h2>
            {appliesBecause?.length > 0 && (
              <div className="mt-2 inline-flex items-start gap-1.5 px-2.5 py-1 rounded-full text-[11px]" style={{ background: "rgba(29,78,216,0.08)", color: "#1D4ED8" }}>
                <Icons.Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Shown because your profile indicates {appliesBecause.map(f => HUMAN_FLAG[f] || f.replace(/_/g, " ")).join(" & ")}</span>
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="label-cap">Section score</div>
            <div className="num-display text-2xl tnum" style={{ color: progressPct >= 80 ? "#059669" : progressPct >= 60 ? "#D97706" : "#DC2626" }}>{progressPct}%</div>
            <div className="text-[10.5px] muted">{sec?.questions_applicable - sec?.unanswered || 0} / {sec?.questions_applicable || 0} answered</div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={q.id} className="stagger-in" style={{ "--stagger": Math.min(i, 8) }}>
            <QuestionCard
              q={q}
              answer={answers[q.id]}
              evidenceList={evidenceMap[q.id] || []}
              onChange={onChange}
              onUpload={onUpload}
              onDeleteEvidence={onDeleteEvidence}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Btn variant="secondary" size="sm" onClick={onPrev} data-testid="wizard-prev">
          <Icons.ArrowLeft className="w-3.5 h-3.5" /> Back
        </Btn>
        <Btn size="sm" onClick={onNext} data-testid="wizard-next">
          {isLast ? "Review & finish" : "Next section"} <Icons.ArrowRight className="w-3.5 h-3.5" />
        </Btn>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Review step
// -----------------------------------------------------------------------------
function ReviewStep({ breakdown, score, answers, onComplete, onPrev, completing, sections }) {
  const critGaps = Object.values(answers || {}).filter(a => a.answer === "no" && ["critical", "high"].includes(a.risk_level));
  const partialGaps = Object.values(answers || {}).filter(a => a.answer === "partial");
  const unanswered = sections.reduce((sum, s) => sum + ((breakdown?.find(b => b.section === s.section)?.unanswered) || 0), 0);
  const scoreColor = score >= 80 ? "#059669" : score >= 60 ? "#D97706" : "#DC2626";

  return (
    <div className="space-y-5 animate-in">
      <div className="aurora rounded-2xl p-6 relative overflow-hidden" style={{ border: "1px solid var(--border)" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="label-cap mb-2">Final step · Review</div>
            <h2 className="text-2xl font-semibold ink tracking-tight">Your DPDP posture</h2>
            <p className="text-[12px] muted mt-1">Completing will generate a Gap Register + Tasks + PDF report.</p>
          </div>
          <div className="text-center">
            <div className="text-[10.5px] muted uppercase tracking-widest">Overall score</div>
            <div className="text-6xl font-bold tnum leading-none mt-1" style={{ color: scoreColor }}>{score}</div>
            <div className="text-[11px] mono muted mt-1">of 100</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-lg" style={{ background: "rgba(220,38,38,0.10)" }}>
              <div className="text-2xl font-bold tnum" style={{ color: "#DC2626" }}>{critGaps.length}</div>
              <div className="text-[10px] font-medium" style={{ color: "#DC2626" }}>Critical/High</div>
            </div>
            <div className="p-2 rounded-lg" style={{ background: "rgba(217,119,6,0.10)" }}>
              <div className="text-2xl font-bold tnum" style={{ color: "#D97706" }}>{partialGaps.length}</div>
              <div className="text-[10px] font-medium" style={{ color: "#D97706" }}>Partial</div>
            </div>
            <div className="p-2 rounded-lg" style={{ background: "rgba(107,114,128,0.10)" }}>
              <div className="text-2xl font-bold tnum muted">{unanswered}</div>
              <div className="text-[10px] font-medium muted">Unanswered</div>
            </div>
          </div>
        </div>
      </div>

      <div className="surface rounded-2xl p-5" style={{ border: "1px solid var(--border)" }}>
        <div className="label-cap mb-3">Section breakdown</div>
        <div className="space-y-2">
          {(breakdown || []).map(b => (
            <div key={b.section} className="flex items-center gap-3">
              <div className="text-[12.5px] ink flex-1 min-w-0 truncate">{b.section}</div>
              <div className="text-[10.5px] muted mono w-16 text-right">{b.dpdp_ref}</div>
              <div className="w-32 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${b.score}%`, background: b.score >= 80 ? "#059669" : b.score >= 60 ? "#D97706" : "#DC2626" }} />
              </div>
              <div className="w-10 text-right text-[12px] font-semibold tnum" style={{ color: b.score >= 80 ? "#059669" : b.score >= 60 ? "#D97706" : "#DC2626" }}>{b.score}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Btn variant="secondary" size="sm" onClick={onPrev} data-testid="wizard-prev-review"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back</Btn>
        <Btn onClick={onComplete} disabled={completing} data-testid="wizard-complete">
          {completing ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.CheckCheck className="w-3.5 h-3.5" />}
          Complete & generate report
        </Btn>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function Assessment() {
  const [schema, setSchema] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [flags, setFlags] = useState({});
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});      // {qid: row}
  const [breakdown, setBreakdown] = useState([]);
  const [score, setScore] = useState(0);
  const [evidenceMap, setEvidenceMap] = useState({});  // {qid: [ev]}
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [step, setStep] = useState(0);   // 0=profile, 1..N=sections, N+1=review
  const saveTimer = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: sc }, { data: pr }, { data: appl }, { data: list }] = await Promise.all([
        api.get("/company-profile/schema"),
        api.get("/company-profile"),
        api.get("/assessments/applicable"),
        api.get("/assessments"),
      ]);
      setSchema(sc); setProfile(pr);
      setSections(appl.sections || []);
      setQuestions(appl.questions || []);
      setFlags(appl.flags || {});
      let a = list.find(x => x.status !== "completed") || null;
      if (!a) {
        const { data } = await api.post("/assessments/start");
        a = data;
      }
      setAssessment(a);
      const map = {};
      (a.answers || []).forEach(x => { map[x.question_id] = x; });
      setAnswers(map);
      setBreakdown(a.section_breakdown || []);
      setScore(a.draft_score || 0);

      // Load evidence per question
      const evReq = Object.keys(map).map(async (qid) => {
        try {
          const { data } = await api.get(`/assessments/${a.id}/answers/${qid}/evidence`);
          return [qid, data];
        } catch { return [qid, []]; }
      });
      const evEntries = await Promise.all(evReq);
      const evMap = {};
      evEntries.forEach(([qid, arr]) => { evMap[qid] = arr; });
      setEvidenceMap(evMap);

      // If profile has enough flags, skip to section step
      const hasSomeFlags = Object.values(appl.flags || {}).some(v => v);
      const hasAnyAnswers = (a.answers || []).length > 0;
      if (hasSomeFlags || hasAnyAnswers) setStep(1);
    } catch (e) {
      toast.error("Failed to load assessment");
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  // Debounced auto-save
  const persistAnswers = (rows) => {
    if (!assessment) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/assessments/${assessment.id}/answers`,
          { answers: rows });
        setBreakdown(data.section_breakdown || []);
        setScore(data.draft_score || 0);
        // Server also normalises; sync back
        const map = {};
        (data.answers || []).forEach(x => { map[x.question_id] = x; });
        setAnswers(map);
      } catch (e) {
        toast.error("Auto-save failed — retry");
      }
    }, 500);
  };

  const changeAnswer = (q, patch) => {
    const existing = answers[q.id] || {
      question_id: q.id, question: q.question, section: q.section,
      answer: "",
    };
    const next = { ...existing, ...patch };
    const nextAnswers = { ...answers, [q.id]: next };
    setAnswers(nextAnswers);
    persistAnswers(Object.values(nextAnswers));
  };

  const uploadEvidence = async (qid, file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post(`/assessments/${assessment.id}/answers/${qid}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setEvidenceMap(prev => ({ ...prev, [qid]: [data, ...(prev[qid] || [])] }));
    setAnswers(prev => ({
      ...prev, [qid]: { ...(prev[qid] || {}),
                          evidence_ids: [...((prev[qid] || {}).evidence_ids || []), data.id] },
    }));
    toast.success("Uploaded");
  };

  const deleteEvidence = async (qid, eid) => {
    try {
      await api.delete(`/assessments/${assessment.id}/answers/${qid}/evidence/${eid}`);
      setEvidenceMap(prev => ({ ...prev, [qid]: (prev[qid] || []).filter(e => e.id !== eid) }));
      setAnswers(prev => ({
        ...prev, [qid]: { ...(prev[qid] || {}),
                            evidence_ids: (prev[qid]?.evidence_ids || []).filter(x => x !== eid) },
      }));
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const saveProfile = async (fields) => {
    const { data } = await api.post("/company-profile/bulk", { fields });
    // Reload applicability
    const [{ data: pr }, { data: appl }] = await Promise.all([
      api.get("/company-profile"),
      api.get("/assessments/applicable"),
    ]);
    setProfile(pr);
    setSections(appl.sections || []);
    setQuestions(appl.questions || []);
    setFlags(appl.flags || {});
    return data;
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/assessments/${assessment.id}/complete`);
      toast.success(`Assessment completed · Score ${data.score}. ${data.gaps_created || 0} gaps created.`);
      window.location.href = "/app/gaps";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    }
    setCompleting(false);
  };

  // Steps: 0=profile, 1..N=sections, N+1=review
  const totalSteps = 2 + (sections?.length || 0);   // profile + N sections + review

  if (loading) return <div className="p-8"><Loader /></div>;

  const currentSection = step >= 1 && step <= sections.length ? sections[step - 1] : null;
  const currentQs = currentSection ? questions.filter(q => q.section === currentSection.section) : [];
  const appliesBecause = currentSection ? (SECTION_FLAG_HINT[currentSection.section] || []).filter(f => flags[f]) : [];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <PageHeader
        kicker="Assessment · Guided wizard"
        title="DPDP Readiness Assessment"
        description="A guided, profile-tailored questionnaire. Your Company Profile drives which sections appear. Each question captures answer, risk, gap, action, owner, target date, status and evidence."
        testId="assessment-wizard"
        actions={
          <>
            <Btn variant="secondary" size="sm" onClick={() => downloadFile(`/assessments/${assessment.id}/report`, `assessment_${assessment.id.slice(0, 8)}.pdf`)} data-testid="assess-pdf-btn"><Icons.FileDown className="w-3.5 h-3.5" /> PDF</Btn>
            <div className="text-[11px] muted ml-2 flex items-center gap-1"><Icons.Save className="w-3 h-3" /> auto-saved</div>
          </>
        }
      />

      {/* Wizard progress bar */}
      <div className="mb-6" data-testid="wizard-progress">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider muted">Step {step + 1} / {totalSteps}</div>
          <div className="text-[11px] muted">
            {step === 0 ? "Company Profile" : step === totalSteps - 1 ? "Review" : currentSection?.section}
          </div>
          <div className="ml-auto text-[11px] font-semibold tnum" style={{ color: score >= 80 ? "#059669" : score >= 60 ? "#D97706" : "#DC2626" }}>
            Score {score}%
          </div>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const done = i < step;
            const active = i === step;
            const c = done ? "#059669" : active ? "var(--primary)" : "var(--border-strong)";
            return (
              <div key={i} className="flex-1 h-1.5 rounded-full transition-colors duration-300" style={{ background: c }} />
            );
          })}
        </div>
      </div>

      {step === 0 && (
        <ProfileStep
          schema={schema} profile={profile}
          onSave={saveProfile}
          onSkip={() => setStep(1)}
          onNext={() => setStep(1)}
        />
      )}
      {step >= 1 && step <= sections.length && currentSection && (
        <SectionStep
          section={currentSection}
          questions={currentQs}
          answers={answers}
          evidenceMap={evidenceMap}
          onChange={changeAnswer}
          onUpload={uploadEvidence}
          onDeleteEvidence={deleteEvidence}
          onPrev={() => setStep(s => Math.max(0, s - 1))}
          onNext={() => setStep(s => s + 1)}
          isLast={step === sections.length}
          breakdown={breakdown}
          appliesBecause={appliesBecause}
        />
      )}
      {step === totalSteps - 1 && step > 0 && (
        <ReviewStep
          breakdown={breakdown} score={score} answers={answers}
          onComplete={complete} onPrev={() => setStep(s => s - 1)}
          completing={completing} sections={sections}
        />
      )}
    </div>
  );
}
